import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, ArrowLeft, CalendarDays } from 'lucide-react';
import { AppDispatch, RootState } from 'slices';
import { fetchSalesReportDetail, clearDetailError } from 'slices/salesReport/salesReportSlice';

interface LocationState {
  market_id?: number;
  session?: string;
  date?: string;
  game_display?: string;
}

type DateFilter =
  | 'today'
  | 'weekly'
  | 'monthly'
  | 'all_time'
  | 'custom';

const formatApiDate = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPresetRange = (
  filter: DateFilter
): {
  dateFrom?: string;
  dateTo?: string;
  allTime?: boolean;
} => {
  const today = new Date();
  const todayString = formatApiDate(today);

  if (filter === 'today') {
    return {
      dateFrom: todayString,
      dateTo: todayString
    };
  }

  if (filter === 'weekly') {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);

    return {
      dateFrom: formatApiDate(start),
      dateTo: todayString
    };
  }

  if (filter === 'monthly') {
    const start = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    return {
      dateFrom: formatApiDate(start),
      dateTo: todayString
    };
  }

  if (filter === 'all_time') {
    return {
      allTime: true
    };
  }

  return {};
};

const SalesData: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { detail, detailLoading, detailError } = useSelector((state: RootState) => state.SalesReport);
  
  const state = (location.state as LocationState) || {};
  const { market_id, session, date, game_display } = state;

  const todayString = formatApiDate(new Date());

  const [dateFilter, setDateFilter] = useState<DateFilter>(
    date === todayString ? 'today' : 'custom'
  );

  const [customDateFrom, setCustomDateFrom] = useState(
    date || ''
  );

  const [customDateTo, setCustomDateTo] = useState(
    date || ''
  );

  const [customDateError, setCustomDateError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Track if we've already fetched to prevent double calls
  const hasFetchedRef = useRef(false);
  const lastFetchParamsRef = useRef<string | null>(null);

  // Fetch detail data whenever the selected date filter changes.
  useEffect(() => {
    if (!market_id || !date) {
      return;
    }

    const effectiveSession = session || 'all';

    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    let allTime = false;

    if (dateFilter === 'custom') {
      if (!customDateFrom || !customDateTo) {
        return;
      }

      if (customDateFrom > customDateTo) {
        setCustomDateError(
          'From date cannot be after To date.'
        );
        return;
      }

      const currentToday = formatApiDate(new Date());

      if (customDateTo > currentToday) {
        setCustomDateError(
          'Future dates are not allowed.'
        );
        return;
      }

      setCustomDateError('');
      dateFrom = customDateFrom;
      dateTo = customDateTo;
    } else {
      setCustomDateError('');

      const preset = getPresetRange(dateFilter);

      dateFrom = preset.dateFrom;
      dateTo = preset.dateTo;
      allTime = preset.allTime === true;
    }

    const fetchKey = [
      market_id,
      effectiveSession,
      dateFilter,
      dateFrom || '',
      dateTo || '',
      allTime ? 'all' : '',
      currentPage,
      pageSize
    ].join('-');

    if (lastFetchParamsRef.current === fetchKey) {
      return;
    }

    lastFetchParamsRef.current = fetchKey;
    hasFetchedRef.current = true;

    dispatch(fetchSalesReportDetail({
      market_id,
      session: effectiveSession,
      date,
      date_from: dateFrom,
      date_to: dateTo,
      all_time: allTime,
      page: currentPage,
      page_size: pageSize
    }));

    return () => {
      dispatch(clearDetailError());
      hasFetchedRef.current = false;
    };
  }, [
    dispatch,
    market_id,
    session,
    date,
    dateFilter,
    customDateFrom,
    customDateTo,
    currentPage,
    pageSize
  ]);

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const activeRangeLabel = (() => {
    if (dateFilter === 'today') {
      return 'Today';
    }

    if (dateFilter === 'weekly') {
      return 'Last 7 Days';
    }

    if (dateFilter === 'monthly') {
      return 'This Month';
    }

    if (dateFilter === 'all_time') {
      return 'All Time';
    }

    if (customDateFrom && customDateTo) {
      if (customDateFrom === customDateTo) {
        return customDateFrom;
      }

      return `${customDateFrom} to ${customDateTo}`;
    }

    return 'Custom Date';
  })();

  const isStarline =
    detail?.market.market_type?.trim().toLowerCase() === 'starline';

  const formatDrawDate = (value?: string | null) => {
    if (!value) return '-';
    const parts = value.split('-');
    return parts.length === 3
      ? `${parts[2]}-${parts[1]}-${parts[0]}`
      : value;
  };

  const formatDrawTime = (value?: string | null) => {
    return value ? value.slice(0, 5) : '-';
  };

  const title = game_display
    ? `Game Data - ${game_display}`
    : detail?.market
      ? detail.market.session === 'all'
        ? `Game Data - ${detail.market.name}`
        : `${detail.market.name} - ${detail.market.session.toUpperCase()}`
      : 'Game Data';

  if (!market_id || !date) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Sales Report Detail</h1>
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Missing required parameters. Please go back and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border mb-6">
        <div className="flex items-center justify-between p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <button 
            onClick={() => navigate('/sales-report', { 
              state: { selectedDate: date } // Pass the date back to preserve selected date
            })} 
            className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        <div className="px-6 py-4 border-b bg-gray-50/70">
          <div className="flex flex-col gap-4">

            <div className="flex flex-wrap items-center gap-2">
              <div className="mr-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <CalendarDays className="w-4 h-4" />
                Date Filter
              </div>

              {[
                ['today', 'Today'],
                ['weekly', 'Weekly'],
                ['monthly', 'Monthly'],
                ['all_time', 'All Time'],
                ['custom', 'Custom Date']
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    const nextFilter = value as DateFilter;

                    setDateFilter(nextFilter);
                    setCurrentPage(1);
                    setCustomDateError('');

                    if (
                      nextFilter === 'custom' &&
                      (!customDateFrom || !customDateTo)
                    ) {
                      setCustomDateFrom(date || todayString);
                      setCustomDateTo(date || todayString);
                    }
                  }}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    dateFilter === value
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {dateFilter === 'custom' && (
              <div className="flex flex-wrap items-end gap-3">

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    From Date
                  </label>

                  <input
                    type="date"
                    value={customDateFrom}
                    max={todayString}
                    onChange={(event) => {
                      setCustomDateFrom(event.target.value);
                      setCurrentPage(1);
                      setCustomDateError('');
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    To Date
                  </label>

                  <input
                    type="date"
                    value={customDateTo}
                    min={customDateFrom || undefined}
                    max={todayString}
                    onChange={(event) => {
                      setCustomDateTo(event.target.value);
                      setCurrentPage(1);
                      setCustomDateError('');
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {customDateError && (
                  <p className="pb-2 text-sm font-medium text-red-600">
                    {customDateError}
                  </p>
                )}
              </div>
            )}

            <div className="text-xs text-gray-500">
              Showing:{' '}
              <span className="font-semibold text-gray-700">
                {activeRangeLabel}
              </span>
            </div>

          </div>
        </div>

        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
            <span className="text-gray-600">Loading detail data...</span>
          </div>
        ) : detailError ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-2">Error loading detail data</p>
              <p className="text-red-600 text-sm">{detailError}</p>
              <button
                onClick={() => {
                  let dateFrom: string | undefined;
                  let dateTo: string | undefined;
                  let allTime = false;

                  if (dateFilter === 'custom') {
                    dateFrom = customDateFrom || undefined;
                    dateTo = customDateTo || undefined;
                  } else {
                    const preset = getPresetRange(dateFilter);
                    dateFrom = preset.dateFrom;
                    dateTo = preset.dateTo;
                    allTime = preset.allTime === true;
                  }

                  dispatch(fetchSalesReportDetail({
                    market_id,
                    session: session || 'all',
                    date,
                    date_from: dateFrom,
                    date_to: dateTo,
                    all_time: allTime,
                    page: 1,
                    page_size: 50
                  }));
                }}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        ) : detail ? (
          <>
            {/* Market Info & Statistics */}
            <div className="p-6 border-b">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Total Bets</p>
                  <p className="text-2xl font-bold text-blue-900">{detail.statistics.total_bets}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(detail.statistics.total_amount)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Total Payout</p>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(detail.statistics.total_payout)}</p>
                </div>
                <div className={`rounded-lg p-4 ${detail.statistics.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: detail.statistics.net_profit >= 0 ? '#059669' : '#dc2626' }}>
                    Net Profit
                  </p>
                  <p className="text-2xl font-bold" style={{ color: detail.statistics.net_profit >= 0 ? '#065f46' : '#991b1b' }}>
                    {formatCurrency(detail.statistics.net_profit)}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Market:</span>
                    <span className="ml-2 font-medium text-gray-900">{detail.market.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Session:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {detail.market.session === 'all'
                        ? (isStarline ? 'Timed Draws' : 'Open + Close')
                        : detail.market.session.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Period:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {activeRangeLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bets Table */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Bets List</h2>
                <span className="text-sm text-gray-600">
                  Showing {detail.bets.length} of {detail.pagination.total_records} bets
                </span>
        </div>

              {detail.bets.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-gray-600">No bets found for this market session.</p>
        </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                        <th className="px-4 py-3 text-left">Bet ID</th>
                        <th className="px-4 py-3 text-left">User</th>
                        <th className="px-4 py-3 text-left">Mobile</th>
                        <th className="px-4 py-3 text-left">Bet Type</th>
                        {isStarline && (
                          <>
                            <th className="px-4 py-3 text-left">Selected Number</th>
                            <th className="px-4 py-3 text-left">Draw Date</th>
                            <th className="px-4 py-3 text-left">Draw Time</th>
                          </>
                        )}
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-right">Potential Payout</th>
                        {isStarline && (
                          <th className="px-4 py-3 text-right">Actual Payout</th>
                        )}
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-left">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                      {detail.bets.map((bet) => (
                        <tr key={bet.bet_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{bet.bet_id}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{bet.user_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{bet.user_mobile}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{bet.bet_type}</td>
                          {isStarline && (
                            <>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {bet.selected_number || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {formatDrawDate(bet.session_date)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {formatDrawTime(bet.session_time)}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(bet.amount)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(bet.potential_payout)}</td>
                          {isStarline && (
                            <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                              {formatCurrency(bet.actual_payout ?? 0)}
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              bet.bet_status === 'won' 
                                ? 'bg-green-100 text-green-800' 
                                : bet.bet_status === 'lost'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {bet.bet_status.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatDateTime(bet.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detail.pagination.total_records > 0 && (
            <div className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-4 lg:flex-row lg:items-center lg:justify-between">

              <div className="text-sm text-gray-600">
                Showing page{' '}
                <span className="font-semibold text-gray-900">
                  {detail.pagination.page}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-gray-900">
                  {detail.pagination.total_pages}
                </span>
                {' '}·{' '}
                <span className="font-semibold text-gray-900">
                  {detail.pagination.total_records.toLocaleString('en-IN')}
                </span>{' '}
                bets
              </div>

              <div className="flex flex-wrap items-center gap-2">

                <button
                  type="button"
                  disabled={!detail.pagination.has_previous || detailLoading}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                {Array.from(
                  { length: detail.pagination.total_pages },
                  (_, index) => index + 1
                )
                  .filter((pageNumber) => {
                    const totalPages = detail.pagination.total_pages;
                    const page = detail.pagination.page;

                    if (totalPages <= 7) {
                      return true;
                    }

                    return (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      Math.abs(pageNumber - page) <= 2
                    );
                  })
                  .map((pageNumber, index, visiblePages) => {
                    const previousPage = visiblePages[index - 1];
                    const showEllipsis =
                      previousPage !== undefined &&
                      pageNumber - previousPage > 1;

                    return (
                      <React.Fragment key={pageNumber}>
                        {showEllipsis && (
                          <span className="px-1 text-sm text-gray-400">
                            …
                          </span>
                        )}

                        <button
                          type="button"
                          disabled={detailLoading}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`min-w-[36px] rounded-lg px-3 py-2 text-sm font-semibold transition ${
                            detail.pagination.page === pageNumber
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  type="button"
                  disabled={!detail.pagination.has_next || detailLoading}
                  onClick={() =>
                    setCurrentPage((page) =>
                      Math.min(
                        detail.pagination.total_pages,
                        page + 1
                      )
                    )
                  }
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>

                <select
                  value={pageSize}
                  disabled={detailLoading}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                  className="ml-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>

              </div>
            </div>
          )}
        </div>
          </>
        ) : (
          <div className="p-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600">No data available. Please try again.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesData;


